<template lang="pug">
.content-slot(
  :class="[`placement-${content.placement}`, `image-${content.imagePosition}`, { 'has-copy': hasCopy, linked: wholeSlotIsLink }]"
  :style="spanStyle"
  data-cnstrc-content
  :data-cnstrc-content-id="content.id"
)
  component(
    :is="wholeSlotIsLink ? 'rogue-link' : 'div'"
    :to="wholeSlotIsLink ? content.ctaUrl : undefined"
    :target="wholeSlotIsLink ? linkTarget : undefined"
    :rel="wholeSlotIsLink ? linkRel : undefined"
    @click="wholeSlotIsLink && trackClick($event)"
  )
    DynamicMedia.asset(v-if="mediaConfig" :config="mediaConfig" :ariaLabel="content.header || content.altText")
    .copy(v-if="hasCopy")
      h4.slot-header(v-if="content.header") {{ __(content.header) }}
      .slot-body(v-if="content.body" v-translate v-html="content.body")
      .slot-html(v-if="content.html" v-translate v-html="content.html")
      RogueButton.cta(v-if="showCtaButton" :color="content.buttonColor" @click="onCtaClick") {{ __(content.ctaText) }}
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { MediaConfig } from 'sisu/js/sisuTypes'
import type { CnstrcContentSlot } from '@/js/services/cnstrc/types'
import { useRogue } from '@/js/services/Rogue'

const props = defineProps<{
  content: CnstrcContentSlot,
  // Banner/bottom slots span the full results column; grid slots take card columns.
  full?: boolean,
}>()

const rogue = useRogue()
const router = useRouter()

const hasCopy = computed(() => Boolean(props.content.header || props.content.body || props.content.html || showCtaButton.value))

// Constructor's rule: when no CTA text is given, the asset itself is the link. With CTA
// text we render a button instead so the copy block stays selectable.
const showCtaButton = computed(() => Boolean(props.content.ctaText && props.content.ctaUrl))
const wholeSlotIsLink = computed(() => Boolean(props.content.ctaUrl) && !showCtaButton.value)

const linkTarget = computed(() => props.content.newTab ? '_blank' : undefined)
// noopener also blocks the new tab's window.opener back-reference, which matters for the
// off-site CTAs these rules often point at.
const linkRel = computed(() => props.content.newTab ? 'noopener noreferrer' : undefined)

const mediaConfig = computed<MediaConfig | null>(() => {
  const { assetUrl, mobileAssetUrl, altText, mobileAltText } = props.content
  if (!assetUrl && !mobileAssetUrl) return null

  // Constructor stores no intrinsic dimensions for a content asset. Assume a wide banner
  // for full-width slots and a product-card square in the grid; DynamicMedia only uses
  // these for the aspect ratio and the Cloudinary resize width.
  const width = 1920
  const height = props.full ? 480 : 1920

  return {
    src    : assetUrl || mobileAssetUrl,
    alt    : altText || props.content.header || '',
    width,
    height,
    mobile : mobileAssetUrl
      ? { src: mobileAssetUrl, alt: mobileAltText || altText || props.content.header || '', width: 780, height: props.full ? 520 : 780 }
      : undefined,
  }
})

const spanStyle = computed(() => props.full || props.content.gridSpan <= 1 ? undefined : { '--slot-span': props.content.gridSpan })

// Both CTA shapes — the whole-asset link and the button — report the same event, so a
// rule's clicks aggregate the same way however it was authored.
function trackClick(event: Event) {
  rogue.track.track({
    type       : 'elClick',
    location   : 'plp-content-slot',
    el         : (event.currentTarget ?? event.target) as HTMLElement,
    link       : props.content.ctaUrl,
    text       : props.content.ctaText || props.content.header,
    // The rule id ties clicks back to the Constructor content rule that produced them.
    elementKey : `cnstrc-content-${props.content.id}`,
  })
}

// RogueButton only re-emits `click` when it isn't handling the navigation itself, so the
// button is left as a plain button and the link behavior is driven from here — otherwise
// RogueButton would fire its own tracking event and this one could never run.
function onCtaClick(event: Event) {
  trackClick(event)

  const url = props.content.ctaUrl
  if (!url) return

  if (props.content.newTab) {
    if (rogue.appContext) {
      rogue.emitToApp('open_in_browser', { url })
    } else {
      window.open(url, '_blank')?.focus()
    }

    return
  }

  // Mirrors sisu's `redirect` helper, against the app's own router — importing it across
  // the package boundary trips the duplicate vue-router install's Router types. Relative
  // urls route in-app (the router's history base carries the locale prefix); absolute
  // ones leave the SPA, or hand off to the native shell in app context.
  if (!url.startsWith('http')) {
    router.push(url)
  } else if (rogue.appContext) {
    rogue.emitToApp('linkclick', { href: url })
  } else {
    window.location.href = url
  }
}
</script>

<style lang="scss" scoped>
.content-slot {
  width: 100%;
  margin-bottom: $space-m;

  // The whole-asset link wrapper. The CTA is a RogueButton, not an anchor, so this can't
  // reach it.
  a {
    color: inherit;
    text-decoration: none;
    display: block;
  }
}

.asset {
  width: 100%;
}

.copy {
  padding: $space-s 0;
}

// Matches the facet accordion headers, which are also an h4 trimmed by a class: the
// global heading rules supply the condensed uppercase face and the margin, and the size
// comes down to $size-s exactly as .filter-header does.
.slot-header {
  font-size: $size-s;
  margin-bottom: $space-xxs;
}

// Matches the product name in a hover-card tile (.title).
.slot-body {
  font-size: $size-xs;
  color: $color-black;
}

// Appearance comes from RogueButton via the buttonColor tag; only spacing belongs here.
.cta {
  margin-top: $space-xs;
}

// image_position:left / :right put the asset beside the copy instead of above it.
// Both stack on mobile, where there is no room for a side-by-side split.
.image-left,
.image-right {
  &.has-copy > :deep(*) {
    @include all-desktop() {
      display: flex;
      align-items: center;
      gap: $space-m;

      > .asset {
        flex: 1 1 50%;
      }

      > .copy {
        flex: 1 1 50%;
      }
    }
  }
}

.image-right.has-copy > :deep(*) {
  @include all-desktop() {
    flex-direction: row-reverse;
  }
}

.placement-grid {
  // Card slots inherit the products-wrapper flex-basis; the span tag widens them by a
  // whole number of columns.
  --slot-span: 1;

  // Match hover-card's box so the asset lines up with the thumbnails beside it: the
  // product's .hover-card is inset $space-xxs on all four sides, and its .main-img caps
  // at 361px so the image stops growing once a column is wider than that.
  padding: $space-xxs;
  margin-bottom: 0;
  position: relative;

  .asset {
    max-width: calc(361px * var(--slot-span));
  }

  // Mirrors the hover-card outline. There the card is absolutely positioned inside
  // .holder and pushes out to -9px on hover; here the slot is the flex item itself, so a
  // pseudo-element grows past it by the same 9px ($space-xxs padding + the 1px border)
  // instead. Desktop only, matching hover-card — there is no hover state on touch.
  @include all-desktop() {
    &:hover {
      z-index: 1;

      &::before {
        content: "";
        position: absolute;
        inset: -9px;
        z-index: -1;
        border: $border-width-thin solid $color-gray-light;
        border-radius: 2px;
        background-color: $color-white;
      }
    }
  }
}
</style>
